<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-white">
    <!-- Hero Section -->
    <section class="relative py-20 bg-gradient-to-r from-green-700 via-teal-400 to-green-900 text-white overflow-hidden">
      <div class="absolute inset-0 bg-black/10"></div>
      <div class="absolute inset-0">
        <div class="absolute top-20 left-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-5 sm:px-10 md:px-12 lg:px-5 py-24">
        <div class="text-center space-y-6" data-aos="fade-up" data-aos-duration="1000">
          <div class="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm">
            <span class="w-2 h-2 bg-white rounded-full mr-2"></span>
            Get In Touch
          </div>
          
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
            Contact Us
          </h1>
          
          <p class="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
            Ready to start your environmental project? Reach out to our expert team for comprehensive consultation and customized solutions
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Content -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-5 sm:px-10 md:px-12 lg:px-5">
        <div class="grid lg:grid-cols-2 gap-16 items-start">
          <!-- Contact Information -->
          <div class="space-y-8" data-aos="fade-right" data-aos-duration="1000">
            <div>
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p class="text-lg text-gray-600 leading-relaxed">
                Our team of environmental experts is ready to assist you with your project needs. Contact us for consultations, assessments, and compliance solutions.
              </p>
            </div>

            <!-- Contact Cards -->
            <div class="space-y-6">
              <!-- Phone Card -->
              <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                    <div class="space-y-1">
                      <p class="text-gray-700">
                        <a href="tel:0814342904" class="hover:text-green-600 transition-colors">081434 2904</a>
                      </p>
                      <p class="text-gray-700">
                        <a href="tel:0693671343" class="hover:text-green-600 transition-colors">069367 1343</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Card -->
              <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                    <p class="text-gray-700">
                      <a href="mailto:service@enviroassess.co.za" class="hover:text-green-600 transition-colors">service@enviroassess.co.za</a>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Office Hours Card -->
              <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div class="flex items-start space-x-4">
                  <div class="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">Office Hours</h3>
                    <div class="space-y-1 text-gray-700">
                      <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                      <p>Saturday: 9:00 AM - 1:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Services Offered -->
            <div class="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-200">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Our Services Include</h3>
              <ul class="space-y-2 text-gray-700">
                <li class="flex items-center">
                  <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Professional ltenviromonitoring assessment and monitoring solutions
                </li>
                <li class="flex items-center">
                  <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Water Use Licenses
                </li>
                <li class="flex items-center">
                  <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Atmospheric Emissions Licenses
                </li>
                <li class="flex items-center">
                  <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Compliance Monitoring
                </li>
              </ul>
            </div>
          </div>

          <!-- Additional Information -->
          <div class="space-y-8" data-aos="fade-left" data-aos-duration="1000">
            <!-- Why Contact Us -->
            <div class="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h3 class="text-2xl font-bold text-gray-900 mb-6">Why Choose Us?</h3>
              
              <div class="space-y-6">
                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-900 mb-2">Expert Team</h4>
                    <p class="text-gray-600">Qualified ltenviromonitoring professionals with years of experience in assessments and compliance.</p>
                  </div>
                </div>

                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-900 mb-2">Fast Response</h4>
                    <p class="text-gray-600">Quick turnaround times and efficient service delivery to meet your project deadlines.</p>
                  </div>
                </div>

                <div class="flex items-start space-x-4">
                  <div class="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-900 mb-2">24/7 Support</h4>
                    <p class="text-gray-600">Round-the-clock availability for urgent environmental matters and project support.</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Call to Action -->
            <div class="bg-gradient-to-br from-green-600 to-teal-600 rounded-2xl p-8 text-white">
              <h3 class="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p class="text-green-100 mb-6 leading-relaxed">
                Contact us today for a free consultation and let our experts help you navigate your environmental compliance requirements.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";

AOS.init();

export default {
  name: 'ContactUs'
}
</script>

<style scoped>
/* Custom styles for premium contact page */
</style>